export class DeferredMedia extends HTMLElement {
  isPlaying = false;
  mediaPoster = null;
  toggleMediaButton = null;

  connectedCallback() {
    this.mediaPoster = this.querySelector('[data-ref="deferred-media-poster"]');
    this.toggleMediaButton = this.querySelector('[data-ref="toggle-media-button"]');

    this.mediaPoster?.addEventListener('click', this.showDeferredMedia.bind(this));
    this.toggleMediaButton?.addEventListener('click', this.toggleMedia.bind(this));

    if (this.hasAttribute('data-visible-autoplay')) this.observeVisibility();
  }

  disconnectedCallback() {
    this.loadObserver?.disconnect();
    this.playObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onPageVisibility);
  }

  observeVisibility() {
    if (!('IntersectionObserver' in window)) {
      this.loadContent(false);
      this.playMedia();
      return;
    }
    this.loadObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.loadContent(false);
        this.loadObserver.disconnect();
      }
    }, { rootMargin: '300px 0px', threshold: 0 });
    this.playObserver = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      this.isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.15;
      this.syncVisibility();
    }, { threshold: [0, 0.15] });
    this.loadObserver.observe(this);
    this.playObserver.observe(this);
    document.addEventListener('visibilitychange', this.onPageVisibility);
  }

  onPageVisibility = () => this.syncVisibility();

  syncVisibility() {
    if (this.isVisible && !document.hidden) {
      this.loadContent(false);
      if (!this.pausedByUser) this.playMedia();
    } else {
      this.pausedByObserver = true;
      this.pauseMedia();
    }
  }

  bindVisibleVideo(video) {
    if (!(video instanceof HTMLVideoElement)) return;
    video.removeAttribute('autoplay');
    video.preload = 'none';
    video.addEventListener('play', () => {
      this.pausedByObserver = false;
      this.pausedByUser = false;
      this.isPlaying = true;
      this.updatePlayPauseHint(true);
    });
    video.addEventListener('pause', () => {
      if (!this.pausedByObserver && this.isVisible) this.pausedByUser = true;
      this.isPlaying = false;
      this.updatePlayPauseHint(false);
    });
    video.addEventListener('ended', () => {
      this.isPlaying = false;
      this.updatePlayPauseHint(false);
    });
  }

  updatePlayPauseHint(isPlaying) {
    if (this.toggleMediaButton instanceof HTMLElement) {
      this.toggleMediaButton.classList.remove('hidden');

      const playIcon = this.toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);

      const pauseIcon = this.toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  showDeferredMedia = () => {
    this.pausedByUser = false;
    this.loadContent(true);
    this.playMedia();
  };

  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    if (this.hasAttribute('data-visible-autoplay')) this.bindVisibleVideo(content);

    this.setAttribute('data-media-loaded', 'true');

    this.querySelector('[data-ref="deferred-media-video"]')?.appendChild(content);

    // this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.querySelector('[ref="deferred-media-poster"]')?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.hasAttribute('autoplay')) {
      content.play()?.catch(() => {});
    }
  }

  toggleMedia() {
    if (this.isPlaying) {
      this.pausedByUser = true;
      this.pausedByObserver = false;
      this.pauseMedia();
    } else {
      this.pausedByUser = false;
      this.playMedia();
    }
  }

  playMedia() {
    if (this.hasAttribute('data-visible-autoplay')) this.loadContent(false);
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*',
      );
    } else {
      const video = this.querySelector('video');
      video?.play()?.catch(() => {
        this.isPlaying = false;
        this.updatePlayPauseHint(false);
      });
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*',
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}
